import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import {
  CheckCircle2, AlertTriangle, XCircle, HelpCircle, ShieldCheck,
  ArrowLeft, Plus, Loader2, RefreshCw, Coins,
} from "lucide-react";

const PAGE_SIZE = 100;

// Color-coded badge per Mailgun validation result
function ResultBadge({ m }) {
  if (m.status !== "validated") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500">
        <HelpCircle className="h-3 w-3" /> Unvalidated
      </span>
    );
  }
  const r = m.result || "unknown";
  const risk = m.risk || "unknown";
  if (r === "deliverable" && risk === "low") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Deliverable
      </span>
    );
  }
  if (r === "deliverable") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700">
        <AlertTriangle className="h-3 w-3" /> {risk} risk
      </span>
    );
  }
  if (r === "undeliverable" || r === "do_not_send") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700">
        <XCircle className="h-3 w-3" /> {r === "do_not_send" ? "Do not send" : "Undeliverable"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">
      <HelpCircle className="h-3 w-3" /> Unknown
    </span>
  );
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unvalidated", label: "Unvalidated" },
  { key: "deliverable", label: "Deliverable" },
  { key: "risky", label: "Risky" },
  { key: "undeliverable", label: "Undeliverable" },
];

export default function ValidationList() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [balance, setBalance] = useState(0);
  const [validating, setValidating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([
        api.get(`/validations/lists/${listId}?page=${page}&limit=${PAGE_SIZE}&filter=${filter}`),
        api.get("/validations/credits"),
      ]);
      setList(l.data);
      setBalance(c.data.balance || 0);
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 404) navigate("/validations");
    }
  }, [listId, page, filter, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  // Reset selection when page/filter changes
  useEffect(() => { setSelected(new Set()); }, [page, filter]);

  const members = list?.members || [];
  const totalFiltered = list?.total_filtered || 0;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));

  const allOnPageSelected = useMemo(() => (
    members.length > 0 && members.every((m) => selected.has(m.email))
  ), [members, selected]);

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        members.forEach((m) => next.delete(m.email));
      } else {
        members.forEach((m) => next.add(m.email));
      }
      return next;
    });
  };

  const toggleOne = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  // Only unvalidated selections count toward the cost
  const billableCount = useMemo(() => {
    return members.filter((m) => selected.has(m.email) && m.status === "unvalidated").length;
  }, [members, selected]);

  const runValidation = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one address to validate");
      return;
    }
    if (billableCount === 0) {
      toast.info("Selected addresses are already validated. Pick unvalidated rows.");
      return;
    }
    if (balance < billableCount) {
      toast.error(`Not enough credits — you have ${balance}, need ${billableCount}. Buy credits to continue.`);
      return;
    }
    if (!window.confirm(
      `Validate ${billableCount} address${billableCount === 1 ? "" : "es"}? This will use ${billableCount} credit${billableCount === 1 ? "" : "s"} from your balance of ${balance}.`
    )) return;
    setValidating(true);
    try {
      const emails = Array.from(selected);
      const { data } = await api.post(`/validations/lists/${listId}/validate`, { emails });
      toast.success(`Validated ${data.validated} addresses. Balance: ${data.balance_after} credits.`);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setValidating(false);
    }
  };

  if (!list) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <main className="max-w-6xl mx-auto px-5 py-10">
          <div className="text-center text-slate-400 text-sm py-20">Loading list…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-6xl mx-auto px-5 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div className="min-w-0">
            <Link
              to="/validations"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#475569] hover:text-[#0F172A] mb-3"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to lists
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight truncate" title={list.name}>
              {list.name}
            </h1>
            <p className="mt-1 text-sm text-[#475569]">
              <span className="mono font-semibold">{list.validated_count.toLocaleString()}</span> of{" "}
              <span className="mono font-semibold">{list.member_count.toLocaleString()}</span> validated
              {list.last_validated_at && (
                <> · Last run {new Date(list.last_validated_at).toLocaleString()}</>
              )}
            </p>
          </div>
          <div
            className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-xl px-4 py-2"
            data-testid="list-balance"
          >
            <Coins className="h-4 w-4 text-[#F26953]" />
            <span className="text-xs uppercase tracking-wider font-bold text-slate-500">Balance</span>
            <span className="font-bold text-[#0F172A] mono">{balance.toLocaleString()}</span>
          </div>
        </div>

        {/* Filter pills + actions */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => { setFilter(f.key); setPage(1); }}
                data-testid={`filter-${f.key}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  filter === f.key
                    ? "bg-[#0F172A] text-white"
                    : "bg-white text-[#475569] hover:bg-slate-100 border border-[#E2E8F0]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <span className="text-xs text-[#475569]" data-testid="selection-counter">
                {selected.size} selected · {billableCount} new
              </span>
            )}
            <button
              onClick={refresh}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#475569] hover:bg-slate-100 px-3 py-1.5 rounded-lg border border-[#E2E8F0]"
              data-testid="list-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={runValidation}
              disabled={selected.size === 0 || validating}
              data-testid="validate-selected-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#F26953] hover:bg-[#dd5a47] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Validate selected ({billableCount})
            </button>
          </div>
        </div>

        {/* Members table */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          {members.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400" data-testid="list-empty-state">
              {filter === "all"
                ? "This list has no addresses yet."
                : `No ${filter} addresses on this page.`}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-[#E2E8F0]">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAll}
                      data-testid="select-all-cbx"
                      className="rounded border-slate-300 text-[#F26953] focus:ring-[#F26953]/30"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Email</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Suggestion</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Last checked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {members.map((m) => (
                  <tr
                    key={m.email}
                    className={`hover:bg-slate-50/50 ${selected.has(m.email) ? "bg-[#FEE7E1]/30" : ""}`}
                    data-testid={`member-row-${m.email}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(m.email)}
                        onChange={() => toggleOne(m.email)}
                        data-testid={`member-cbx-${m.email}`}
                        className="rounded border-slate-300 text-[#F26953] focus:ring-[#F26953]/30"
                      />
                    </td>
                    <td className="px-4 py-3 mono text-[#0F172A]">
                      {m.email}
                      {(m.is_disposable || m.is_role) && (
                        <span className="ml-2 text-[10px] text-slate-400">
                          {m.is_disposable && <span className="text-amber-600">· disposable</span>}
                          {m.is_role && <span className="text-amber-600">· role</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><ResultBadge m={m} /></td>
                    <td className="px-4 py-3 text-xs text-[#475569] mono">
                      {m.did_you_mean ? <span className="text-amber-700">→ {m.did_you_mean}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {m.validated_at ? new Date(m.validated_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-slate-400">
              Page {page} of {totalPages} · {totalFiltered.toLocaleString()} addresses
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="list-prev-page"
                className="px-3 py-1.5 text-xs font-semibold border border-[#E2E8F0] rounded-lg hover:bg-slate-100 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="list-next-page"
                className="px-3 py-1.5 text-xs font-semibold border border-[#E2E8F0] rounded-lg hover:bg-slate-100 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
